import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Loader2, Check, X, Search, MoreHorizontal, Copy, Eye, EyeOff, Archive, ArchiveRestore, Star, ImagePlus, GripVertical, Sparkles, AlertTriangle, Wand2, Send, Link2 } from "lucide-react";
import { adminApi } from "@/admin/adminApi";
import { getSchema } from "@/admin/schemas";
import BulkUpload from "@/admin/components/BulkUpload";
import { uploadMedia, absUrl, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const getVal = (obj, path) => path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);
function setVal(obj, path, val) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) { cur[keys[i]] = { ...(cur[keys[i]] || {}) }; cur = cur[keys[i]]; }
  cur[keys[keys.length - 1]] = val;
  return clone;
}

function toFormValue(field, item) {
  const v = getVal(item || {}, field.key);
  if (field.type === "tags" || field.type === "collections") return Array.isArray(v) ? v.join(", ") : (v || "");
  if (field.type === "lines") return Array.isArray(v) ? v.join("\n") : (v || "");
  if (field.type === "boolean") return !!v;
  if (field.type === "number") return v ?? "";
  return v ?? "";
}

function buildPayload(schema, form, defaults) {
  let payload = { ...defaults };
  schema.fields.forEach((f) => {
    const raw = form[f.key];
    let val;
    if (f.type === "number") val = raw === "" || raw === null || raw === undefined ? undefined : Number(raw);
    else if (f.type === "boolean") val = !!raw;
    else if (f.type === "tags" || f.type === "collections") val = String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
    else if (f.type === "lines") val = String(raw || "").split("\n").map((s) => s.trim()).filter(Boolean);
    else val = raw ?? "";
    if (val !== undefined) payload = setVal(payload, f.key, val);
  });
  if (schema.hasGallery) {
    const g = (form.__gallery || []).filter(Boolean);
    payload.image = g[0] || "";
    payload.images = g.slice(1);
  }
  return payload;
}

// Resize/compress in the browser so owners can upload straight from phone/camera.
async function compressImage(file, maxDim = 1600, quality = 0.82) {
  if (!file || !file.type?.startsWith("image/") || file.type === "image/gif") return file;
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    width = Math.round(width * scale); height = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = width; canvas.height = height;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

/* ---------------- single image field (collections, reviews, etc.) ---------------- */
function ImageField({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const data = await uploadMedia(await compressImage(file), "photo", "admin");
      onChange(absUrl(data.media_url));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-wp-md border border-border bg-wp-cream-100">
        {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full w-full place-items-center text-wp-ink-muted"><Upload className="h-4 w-4" /></div>}
      </div>
      <div className="flex-1 space-y-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="rounded-wp-md" data-testid={testid} />
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={pick} />
        <Button type="button" variant="outline" size="sm" className="rounded-lg" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />} Upload
        </Button>
      </div>
    </div>
  );
}

/* ---------------- multi-image gallery manager (products & parcels) ---------------- */
function GalleryManager({ images, onChange }) {
  const inputRef = useRef(null);
  const replaceRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const fromIdx = useRef(null);
  const [overIdx, setOverIdx] = useState(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const data = await uploadMedia(await compressImage(f), "photo", "admin");
        uploaded.push(absUrl(data.media_url));
      }
      onChange([...(images || []), ...uploaded]);
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} added`);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };

  const doReplace = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || replaceIdx == null) return;
    setBusy(true);
    try {
      const data = await uploadMedia(await compressImage(file), "photo", "admin");
      const arr = [...images]; arr[replaceIdx] = absUrl(data.media_url); onChange(arr);
      toast.success("Photo replaced");
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); setReplaceIdx(null); }
  };

  const removeAt = (i) => onChange(images.filter((_, idx) => idx !== i));
  const makePrimary = (i) => { const arr = [...images]; const [x] = arr.splice(i, 1); arr.unshift(x); onChange(arr); };
  const onDrop = (to) => {
    const from = fromIdx.current;
    if (from == null || from === to) { setOverIdx(null); return; }
    const arr = [...images]; const [x] = arr.splice(from, 1); arr.splice(to, 0, x);
    onChange(arr); fromIdx.current = null; setOverIdx(null);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files); }}
        className={`flex flex-col items-center justify-center rounded-wp-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${dragOver ? "border-wp-rose-strong bg-wp-blush-50" : "border-border bg-wp-cream-100"}`}
        data-testid="gallery-dropzone"
      >
        {busy ? <Loader2 className="h-6 w-6 animate-spin text-wp-rose-strong" /> : <ImagePlus className="h-6 w-6 text-wp-rose-strong" />}
        <p className="mt-2 text-sm font-medium text-wp-ink">Drag &amp; drop photos here</p>
        <p className="text-xs text-wp-ink-muted">or upload from your device · added automatically web-optimized</p>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={replaceRef} type="file" accept="image/*" hidden onChange={doReplace} />
        <Button type="button" variant="outline" size="sm" className="mt-3 rounded-lg" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="gallery-upload-button">
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Choose photos
        </Button>
      </div>

      {(images || []).length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2.5 sm:grid-cols-4" data-testid="gallery-grid">
          {images.map((src, i) => (
            <div
              key={src + i}
              draggable
              onDragStart={() => { fromIdx.current = i; }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(i); }}
              onDrop={() => onDrop(i)}
              className={`group relative aspect-square overflow-hidden rounded-wp-md border bg-wp-cream-100 ${overIdx === i ? "border-wp-rose-strong ring-2 ring-wp-rose-strong/40" : "border-border"}`}
              data-testid={`gallery-item-${i}`}
            >
              <img src={src} alt="" className="h-full w-full object-cover" />
              <span className="absolute left-1 top-1 grid h-5 w-5 place-items-center rounded bg-wp-ink/50 text-white"><GripVertical className="h-3 w-3" /></span>
              {i === 0 ? (
                <Badge className="absolute bottom-1 left-1 border-0 bg-wp-rose-strong px-1.5 py-0 text-[9px] text-white">Primary</Badge>
              ) : (
                <button type="button" onClick={() => makePrimary(i)} title="Set as primary"
                  className="absolute bottom-1 left-1 rounded bg-wp-ink/50 p-1 text-white opacity-0 transition group-hover:opacity-100" data-testid={`gallery-primary-${i}`}>
                  <Star className="h-3 w-3" />
                </button>
              )}
              <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition group-hover:opacity-100">
                <button type="button" onClick={() => { setReplaceIdx(i); replaceRef.current?.click(); }} title="Replace"
                  className="rounded bg-wp-ink/50 p-1 text-white hover:bg-wp-ink/70" data-testid={`gallery-replace-${i}`}><Upload className="h-3 w-3" /></button>
                <button type="button" onClick={() => removeAt(i)} title="Delete"
                  className="rounded bg-wp-ink/50 p-1 text-white hover:bg-red-500" data-testid={`gallery-delete-${i}`}><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- inline quick-edit cell ---------------- */
function QuickEditCell({ item, col, onSave }) {
  const raw = getVal(item, col.key);
  const [editing, setEditing] = useState(false);
  const [val, setVal2] = useState(raw ?? "");
  const [saving, setSaving] = useState(false);
  const isNum = col.quickEdit === "number" || col.type === "price";

  const commit = async () => {
    setEditing(false);
    const next = isNum ? (val === "" ? 0 : Number(val)) : val;
    if (next === raw) return;
    setSaving(true);
    try { await onSave(col.key, next); } finally { setSaving(false); }
  };

  if (!editing) {
    return (
      <button type="button" onClick={() => { setVal2(raw ?? ""); setEditing(true); }}
        className="group inline-flex items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-wp-blush-50" data-testid={`quickedit-${col.key}-${getVal(item, "id") || getVal(item, "slug")}`}>
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        <span className={col.type === "price" ? "price-num font-medium" : ""}>{col.type === "price" ? formatINR(raw) : (raw ?? "—")}</span>
        <Pencil className="h-3 w-3 text-wp-ink-muted opacity-0 transition group-hover:opacity-100" />
      </button>
    );
  }
  return (
    <Input autoFocus type={isNum ? "number" : "text"} value={val} onChange={(e) => setVal2(e.target.value)}
      onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
      className="h-8 w-24 rounded-md" />
  );
}

function StatusBadge({ item }) {
  if (item.archived) return <Badge className="border-0 bg-wp-beige-200 text-wp-ink">Archived</Badge>;
  if (item.hidden) return <Badge className="border-0 bg-wp-gold-soft text-wp-ink">Hidden</Badge>;
  return <Badge className="border-0 bg-wp-blush-100 text-wp-rose-strong">Live</Badge>;
}

/* ---------------- searchable collection picker (chips + checkboxes) ---------------- */
function CollectionPicker({ value, onChange }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    let m = true;
    adminApi.get("/resource/collections").then(({ data }) => { if (m) setAll((data || []).filter((c) => !c.archived)); }).catch(() => {});
    return () => { m = false; };
  }, []);
  const selected = String(value || "").split(",").map((s) => s.trim()).filter(Boolean);
  const nameOf = (slug) => all.find((c) => c.slug === slug)?.name || slug;
  const toggle = (slug) => {
    const set = new Set(selected);
    set.has(slug) ? set.delete(slug) : set.add(slug);
    onChange(Array.from(set).join(", "));
  };
  const remove = (slug) => onChange(selected.filter((s) => s !== slug).join(", "));
  const ql = q.trim().toLowerCase();
  const filtered = all.filter((c) => !ql || (c.name || "").toLowerCase().includes(ql) || (c.slug || "").toLowerCase().includes(ql));

  return (
    <div data-testid="collection-picker">
      <div className="flex flex-wrap gap-1.5">
        {selected.length === 0 && <span className="text-xs text-wp-ink-muted">None selected yet — pick below.</span>}
        {selected.map((slug) => (
          <span key={slug} className="inline-flex items-center gap-1 rounded-full bg-wp-blush-100 px-2.5 py-1 text-xs font-medium text-wp-rose-strong" data-testid={`collection-chip-${slug}`}>
            {nameOf(slug)}
            <button type="button" onClick={() => remove(slug)} className="rounded-full p-0.5 hover:bg-wp-rose-strong/15" data-testid={`collection-remove-${slug}`} aria-label={`Remove ${nameOf(slug)}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <div className="mt-2 overflow-hidden rounded-wp-md border border-border">
        <div className="relative border-b border-border">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-wp-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search collections…" className="w-full bg-transparent py-2 pl-8 pr-3 text-sm outline-none" data-testid="collection-search" />
        </div>
        <div className="max-h-44 overflow-y-auto p-1">
          {filtered.length === 0 && <p className="px-2 py-2 text-xs text-wp-ink-muted">No collections found.</p>}
          {filtered.map((c) => {
            const on = selected.includes(c.slug);
            return (
              <button type="button" key={c.slug} onClick={() => toggle(c.slug)}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition hover:bg-wp-blush-50 ${on ? "text-wp-rose-strong" : "text-wp-ink"}`}
                data-testid={`collection-option-${c.slug}`}>
                <span className={`grid h-4 w-4 flex-shrink-0 place-items-center rounded border ${on ? "border-wp-rose-strong bg-wp-rose-strong text-white" : "border-border"}`}>{on && <Check className="h-3 w-3" />}</span>
                <span className="flex-1 truncate">{c.name || c.slug}</span>
                {c.emoji && <span className="text-sm">{c.emoji}</span>}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function ResourceManager({ resource, title, founder = false }) {
  const schema = useMemo(() => getSchema(resource, founder), [resource, founder]);
  const idField = schema.idField || "id";
  const canStatus = ["products", "parcels", "collections"].includes(resource);
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    setItems(null);
    try {
      const { data } = await adminApi.get(`/resource/${resource}`);
      setItems(founder ? data.filter((d) => d.role === "founder") : data);
    } catch { setItems([]); toast.error("Could not load data"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [resource, founder]);

  const openNew = () => {
    const f = {};
    schema.fields.forEach((fl) => { f[fl.key] = toFormValue(fl, schema.defaults || {}); });
    if (schema.hasGallery) f.__gallery = [];
    setForm(f); setEditing({});
  };
  const openEdit = (item) => {
    const f = {};
    schema.fields.forEach((fl) => { f[fl.key] = toFormValue(fl, item); });
    if (schema.hasGallery) f.__gallery = [item.image, ...(item.images || [])].filter(Boolean);
    setForm(f); setEditing(item);
  };

  const save = async () => {
    for (const fl of schema.fields) {
      if (fl.required && !String(form[fl.key] ?? "").trim()) return toast.error(`${fl.label} is required.`);
    }
    setSaving(true);
    try {
      const payload = buildPayload(schema, form, schema.defaults || {});
      const isNew = !editing || !getVal(editing, idField);
      if (isNew) await adminApi.post(`/resource/${resource}`, payload);
      else await adminApi.put(`/resource/${resource}/${getVal(editing, idField)}`, payload);
      toast.success(`${schema.singular} ${isNew ? "created" : "updated"}`);
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminApi.delete(`/resource/${resource}/${getVal(deleteTarget, idField)}`);
      toast.success(`${schema.singular} deleted`);
      setDeleteTarget(null); load();
    } catch { toast.error("Could not delete"); }
  };

  // Optimistic patch used by row-actions + inline quick edit.
  const patchItem = async (item, updates, successMsg) => {
    const id = getVal(item, idField);
    setItems((prev) => (prev || []).map((it) => (getVal(it, idField) === id ? { ...it, ...updates } : it)));
    try {
      await adminApi.put(`/resource/${resource}/${id}`, updates);
      if (successMsg) toast.success(successMsg);
    } catch { toast.error("Could not update"); load(); }
  };

  const duplicate = async (item) => {
    try {
      await adminApi.post(`/resource/${resource}/${getVal(item, idField)}/duplicate`);
      toast.success(`${schema.singular} duplicated (saved as Hidden)`);
      load();
    } catch { toast.error("Could not duplicate"); }
  };

  const filtered = (items || []).filter((it) => {
    if (!query) return true;
    return JSON.stringify(it).toLowerCase().includes(query.toLowerCase());
  });

  const renderCell = (col, item) => {
    if (col.type === "status") return <StatusBadge item={item} />;
    if (col.quickEdit) return <QuickEditCell item={item} col={col} onSave={(k, v) => patchItem(item, { [k]: v })} />;
    const v = getVal(item, col.key);
    if (col.type === "image") return <div className="h-10 w-10 overflow-hidden rounded-md border border-border bg-wp-cream-100">{v ? <img src={v} alt="" className="h-full w-full object-cover" /> : null}</div>;
    if (col.type === "price") return <span className="price-num font-medium">{formatINR(v)}</span>;
    if (col.type === "badge") return v ? <Badge className="bg-wp-blush-100 text-wp-rose-strong border-0">{v}</Badge> : <span className="text-wp-ink-muted">—</span>;
    if (col.type === "boolean") return v ? <Check className="h-4 w-4 text-wp-success" /> : <X className="h-4 w-4 text-wp-ink-muted" />;
    return <span className="line-clamp-1">{v ?? "—"}</span>;
  };

  const galleryCount = (form.__gallery || []).length;

  return (
    <div data-testid={`resource-${resource}${founder ? "-founder" : ""}`}>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl">{title || schema.singular}</h1>
          <p className="text-sm text-wp-ink-muted">{items ? `${filtered.length} item${filtered.length === 1 ? "" : "s"}` : "Loading…"}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-wp-ink-muted" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" className="w-48 rounded-wp-md pl-8" data-testid="resource-search" />
          </div>
          <Button onClick={openNew} className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" data-testid="resource-new-button"><Plus className="mr-1.5 h-4 w-4" /> New</Button>
          {resource === "products" && (
            <Button onClick={() => setBulkOpen(true)} variant="outline" className="rounded-xl border-wp-rose-strong text-wp-rose-strong hover:bg-wp-cream-100" data-testid="smart-bulk-upload-button">
              <Sparkles className="mr-1.5 h-4 w-4" /> Smart Bulk Upload
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-hidden rounded-wp-lg border border-border bg-card shadow-wp-soft">
        {items === null ? (
          <div className="space-y-2 p-4">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-wp-ink-muted">No items yet. Click <span className="font-medium text-wp-ink">New</span> to add one.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {schema.columns.map((c) => <TableHead key={c.key}>{c.label || ""}</TableHead>)}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={getVal(item, idField)} data-testid={`resource-row-${getVal(item, idField)}`} className={item.archived ? "opacity-55" : ""}>
                  {schema.columns.map((c) => <TableCell key={c.key}>{renderCell(c, item)}</TableCell>)}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" onClick={() => openEdit(item)} data-testid={`resource-edit-${getVal(item, idField)}`}><Pencil className="h-4 w-4" /></Button>
                      {canStatus ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg" data-testid={`resource-actions-${getVal(item, idField)}`}><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem onClick={() => duplicate(item)} data-testid={`resource-duplicate-${getVal(item, idField)}`}><Copy className="mr-2 h-4 w-4" /> Duplicate</DropdownMenuItem>
                            {item.hidden ? (
                              <DropdownMenuItem onClick={() => patchItem(item, { hidden: false }, "Now visible")} data-testid={`resource-unhide-${getVal(item, idField)}`}><Eye className="mr-2 h-4 w-4" /> Unhide</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => patchItem(item, { hidden: true }, "Hidden from storefront")} data-testid={`resource-hide-${getVal(item, idField)}`}><EyeOff className="mr-2 h-4 w-4" /> Hide</DropdownMenuItem>
                            )}
                            {item.archived ? (
                              <DropdownMenuItem onClick={() => patchItem(item, { archived: false }, "Restored")} data-testid={`resource-unarchive-${getVal(item, idField)}`}><ArchiveRestore className="mr-2 h-4 w-4" /> Unarchive</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => patchItem(item, { archived: true }, "Archived")} data-testid={`resource-archive-${getVal(item, idField)}`}><Archive className="mr-2 h-4 w-4" /> Archive</DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setDeleteTarget(item)} className="text-red-600 focus:text-red-600"><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <Button size="icon" variant="ghost" className="h-8 w-8 rounded-lg text-wp-rose-strong hover:bg-wp-blush-100" onClick={() => setDeleteTarget(item)} data-testid={`resource-delete-${getVal(item, idField)}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing && getVal(editing, idField) ? `Edit ${schema.singular}` : `New ${schema.singular}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {schema.hasGallery && (
              <div>
                <Label className="mb-1.5 block text-sm">Photos {galleryCount > 0 && <span className="text-wp-ink-muted">· {galleryCount} · first = primary</span>}</Label>
                <GalleryManager images={form.__gallery || []} onChange={(g) => setForm({ ...form, __gallery: g })} />
              </div>
            )}
            {schema.fields.map((fl) => (
              <div key={fl.key}>
                {fl.type !== "boolean" && <Label className="mb-1.5 block text-sm">{fl.label}{fl.required && <span className="text-wp-rose-strong"> *</span>}</Label>}
                {fl.type === "text" && <Input value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} className="rounded-wp-md" data-testid={`field-${fl.key}`} />}
                {fl.type === "number" && <Input type="number" value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} className="rounded-wp-md" data-testid={`field-${fl.key}`} />}
                {fl.type === "textarea" && <Textarea rows={3} value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} className="rounded-wp-md" data-testid={`field-${fl.key}`} />}
                {fl.type === "tags" && <Input value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} placeholder="comma, separated, values" className="rounded-wp-md" data-testid={`field-${fl.key}`} />}
                {fl.type === "collections" && <CollectionPicker value={form[fl.key] ?? ""} onChange={(v) => setForm({ ...form, [fl.key]: v })} />}
                {fl.type === "lines" && <Textarea rows={4} value={form[fl.key] ?? ""} onChange={(e) => setForm({ ...form, [fl.key]: e.target.value })} placeholder="one per line" className="rounded-wp-md" data-testid={`field-${fl.key}`} />}
                {fl.type === "image" && <ImageField value={form[fl.key]} onChange={(v) => setForm({ ...form, [fl.key]: v })} testid={`field-${fl.key}`} />}
                {fl.type === "select" && (
                  <Select value={form[fl.key] ?? ""} onValueChange={(v) => setForm({ ...form, [fl.key]: v })}>
                    <SelectTrigger className="rounded-wp-md" data-testid={`field-${fl.key}`}><SelectValue placeholder="Choose" /></SelectTrigger>
                    <SelectContent>{fl.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                )}
                {fl.type === "boolean" && (
                  <div className="flex items-center justify-between rounded-wp-md border border-border px-3 py-2.5">
                    <Label className="text-sm">{fl.label}</Label>
                    <Switch checked={!!form[fl.key]} onCheckedChange={(v) => setForm({ ...form, [fl.key]: v })} data-testid={`field-${fl.key}`} />
                  </div>
                )}
                {fl.hint && <p className="mt-1 text-xs text-wp-ink-muted">{fl.hint}</p>}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setEditing(null)} data-testid="resource-form-cancel">Cancel</Button>
            <Button className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" onClick={save} disabled={saving} data-testid="resource-form-save">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {schema.singular}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. Tip: you can <span className="font-medium">Archive</span> instead to hide it without losing it.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-wp-rose-strong text-white hover:brightness-105" onClick={doDelete} data-testid="resource-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {resource === "products" && (
        <BulkUpload open={bulkOpen} onOpenChange={setBulkOpen} onPublished={load} />
      )}
    </div>
  );
}
